// @flow

// Lib
import _first from 'lodash/first'
import _isEmpty from 'lodash/isEmpty'
import _forEach from 'lodash/forEach'
import _find from 'lodash/find'

// Effect Manager
import { fork, put, take, select } from 'redux-saga/effects'

// Selectors
import operations$ from '../selectors/operations'
import recommendations$ from '../selectors/recommendations'
import { currentUserUnits$ } from 'hfty-users'
import { fertilizers$ } from 'hfty-fertilizers'
import crops$ from 'fc-model-crops/src/selectors/crops'

// Utils
import { convertRecommendationToFrontendUnits } from '../utils'

// Constants
import {
  PROP_RECOMMENDATION_ID,
  PROP_YIELD_TARGET,
  PROP_CONTROLLER_TYPE,
  PROP_REMOVAL_CROP_GROWN,
  PROP_REMOVAL_YIELD,
  PROP_RECOMMENDATION_TYPE,
  PROP_PREVIOUS_CROP,
  PROP_CREDIT_OTHER_PRESCRIPTIONS,
  PROP_ASSET,
  PROP_CROP,
  PROP_REMOVAL_CROP,
  PROP_TARGET_PH,
  PROP_STATUS,
  RECOMMENDATION_FERTILIZER_SET,
  AG_PHD_MAINTENANCE,
  STATUS,
  SHAPE_FILE,
  ELEMENT_SPLIT_TYPE,
  PROP_CONTROLLER,
  PROP_RXRATES,
  PROP_ELEMENT_PICK,
  PROP_ELEMENT_SPLIT,
  PROP_PERCENT_SPLIT,
} from 'hfty-model-recommendations'

// Components
import datatable from 'hfty-recommendations-dom/src/datatable'

// Action Types
import { ADD_RECOMMENDATION, EDIT_RECOMMENDATION, REMOVE_RECOMMENDATIONS } from '../constants'

// Operator
import recommendationsOperator from 'hfty-operator-recommendations'

// Controllers
import recommendationsCtrl from 'hfty-ctrl-recommendations'
import fertilizersCtrl from 'fc-ctrl-fertilizers'
import usersCtrl from 'fc-ctrl-users'
import { controller as cropsCtrl } from 'fc-model-crops'

// Types
import type { Recommendation } from 'hfty-model-recommendations/types'

// Exports
export default function *watchRecommendationOperatoions (): Generator<*, void, *> {
  while (true) {
    const { payload, type } = yield take([ADD_RECOMMENDATION, EDIT_RECOMMENDATION, REMOVE_RECOMMENDATIONS])
    if (type === ADD_RECOMMENDATION) {
      yield fork(handleAddRecommendation, payload)
    } else if (type === EDIT_RECOMMENDATION) {
      yield fork(handleEditRecommendation, payload)
    } else if (type === REMOVE_RECOMMENDATIONS) {
      yield fork(handleRemoveRecommendations, payload)
    }
  }
}

export function *handleAddRecommendation (payload: Object): Generator<*, void, *> {
  const operations = yield select(operations$)
  const action = yield recommendationsOperator.actionCreators.cancelOperations(operations)
  yield put(action)
  const operation = recommendationsOperator.create({
    model: {
      [RECOMMENDATION_FERTILIZER_SET]: [],
      [PROP_YIELD_TARGET]: null,
      [PROP_CONTROLLER_TYPE]: SHAPE_FILE,
      [PROP_REMOVAL_CROP_GROWN]: false,
      [PROP_REMOVAL_YIELD]: null,
      [PROP_RECOMMENDATION_TYPE]: AG_PHD_MAINTENANCE.value,
      [PROP_PREVIOUS_CROP]: null,
      [PROP_CREDIT_OTHER_PRESCRIPTIONS]: false,
      [PROP_ASSET]: payload.fieldId,
      [PROP_CROP]: null,
      [PROP_REMOVAL_CROP]: null,
      [PROP_TARGET_PH]: null,
      [PROP_STATUS]: _first(STATUS),
      [ELEMENT_SPLIT_TYPE]: {},
    },
  })
  yield put(recommendationsOperator.actionCreators.add(operation))
}

export function *handleEditRecommendation (payload: Object): Generator<*, void, *> {
  const operations = yield select(operations$)
  const action = yield recommendationsOperator.actionCreators.cancelOperations(operations)
  yield put(action)

  let fertilizers = yield select(fertilizers$)

  while (_isEmpty(fertilizers)) {
    yield take(fertilizersCtrl.actionTypes.receive())

    fertilizers = yield select(fertilizers$)
  }

  let recommendations = yield select(recommendations$)

  while (_isEmpty(recommendations)) {
    yield take(recommendationsCtrl.actionTypes.receive())

    recommendations = yield select(recommendations$)
  }

  const recommendation = _find(recommendations, (recommendation: Recommendation): boolean => (
    recommendation[PROP_RECOMMENDATION_ID] === Number(payload.recommendationId)
  )) || {}

  let currentUserUnits = yield select(currentUserUnits$)

  while (_isEmpty(currentUserUnits)) {
    yield take(usersCtrl.actionTypes.receive())

    currentUserUnits = yield select(currentUserUnits$)
  }

  let crops = yield select(crops$)

  while (_isEmpty(crops)) {
    yield take(cropsCtrl.actionTypes.receive())

    crops = yield select(crops$)
  }

  const model = convertRecommendationToFrontendUnits(recommendation, crops, currentUserUnits, fertilizers)

  // Remove unnessary items
  delete model[PROP_RECOMMENDATION_ID]
  delete model[PROP_RXRATES]
  delete model[PROP_RECOMMENDATION_ID]
  delete model[PROP_CONTROLLER]
  model[PROP_STATUS] = _first(STATUS)

  const elementSplitType = {}
  const prescriptions = model[RECOMMENDATION_FERTILIZER_SET]

  // Create the elementSplitType model
  _forEach(prescriptions, (prescription: Object) => {
    if (prescription[PROP_ELEMENT_SPLIT]) {
      elementSplitType[prescription[PROP_ELEMENT_PICK]] = PROP_ELEMENT_SPLIT
    }
    if (prescription[PROP_PERCENT_SPLIT]) {
      elementSplitType[prescription[PROP_ELEMENT_PICK]] = PROP_PERCENT_SPLIT
    }

    // Clean out invalid 0 value for element_split
    if (prescription[PROP_ELEMENT_SPLIT] === 0) {
      prescription[PROP_ELEMENT_SPLIT] = null
    }
  })

  model[ELEMENT_SPLIT_TYPE] = elementSplitType
  const operation = recommendationsOperator.create({
    model: {
      ...model,
      [RECOMMENDATION_FERTILIZER_SET]: prescriptions,
    },
  })
  yield put(recommendationsOperator.actionCreators.add(operation))
}

export function *handleRemoveRecommendations (payload: Object): Generator<*, void, *> {
  const recommendations = yield select(recommendations$)
  const currentUserUnits = yield select(currentUserUnits$)
  const fertilizers = yield select(fertilizers$)
  const crops = yield select(crops$)
  const selectedIds: Array<number> = yield select(datatable.selectors.selectedRecordIds$)
  const operations = selectedIds.map(function (recommendationId: number): * {
    const recommendation: Recommendation = recommendations[recommendationId]
    return {
      model: convertRecommendationToFrontendUnits(recommendation, crops, currentUserUnits, fertilizers),
    }
  })
  yield put(recommendationsOperator.actionCreators.delete(...operations))
}
